import { useState, useEffect } from 'react';

export type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'customized';

const CONSENT_KEY = 'cookie-consent';
const PREFERENCES_KEY = 'cookie-preferences';
const CONSENT_TS_KEY = 'cookie-consent-ts';
const THIRTEEN_MONTHS_MS = 13 * 30 * 24 * 60 * 60 * 1000;

// Pousse l'état de consentement au dataLayer GTM (Consent Mode v2)
const updateGTMConsent = (
  prefs: CookiePreferences,
  meta: { action: 'accept_all' | 'reject_all' | 'customize'; status: ConsentStatus; view: 'banner' | 'settings_modal' }
) => {
  try {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    const timestamp = new Date().toISOString();

    // 1) Consent Mode v2 update (signal natif Google)
    w.dataLayer.push({
      event: 'consent_update',
      analytics_storage: prefs.analytics ? 'granted' : 'denied',
      ad_storage: prefs.marketing ? 'granted' : 'denied',
      ad_personalization: prefs.marketing ? 'granted' : 'denied',
      ad_user_data: prefs.marketing ? 'granted' : 'denied',
      consent_action: meta.action,
      consent_analytics: prefs.analytics,
      consent_marketing: prefs.marketing,
      consent_status: meta.status,
      consent_timestamp: timestamp,
      consent_view: meta.view,
    });

    // 2) Event dédié pour les stats GA4 (balise "GA4 - Event - consent_stats")
    w.dataLayer.push({
      event: 'consent_stats',
      consent_action: meta.action,
      consent_analytics: prefs.analytics,
      consent_marketing: prefs.marketing,
      consent_status: meta.status,
      consent_timestamp: timestamp,
      consent_view: meta.view,
    });

    if (typeof w.gtag === 'function') {
      w.gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.marketing ? 'granted' : 'denied',
        ad_personalization: prefs.marketing ? 'granted' : 'denied',
        ad_user_data: prefs.marketing ? 'granted' : 'denied',
      });
    }
  } catch {
    // no-op
  }
};

// Pousse l'impression de la bannière (balise "GA4 - Event - consent_banner_impression")
export const pushConsentBannerImpression = (view: 'banner' | 'settings_modal' = 'banner') => {
  try {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: 'consent_banner_impression',
      consent_view: view,
      consent_timestamp: new Date().toISOString(),
    });
  } catch {
    // no-op
  }
};

export const useCookieConsent = () => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // CNIL : consentement valable 13 mois max — purge si expiré
    try {
      const ts = parseInt(localStorage.getItem(CONSENT_TS_KEY) || '0', 10);
      if (ts && Date.now() - ts > THIRTEEN_MONTHS_MS) {
        localStorage.removeItem(CONSENT_KEY);
        localStorage.removeItem(PREFERENCES_KEY);
        localStorage.removeItem(CONSENT_TS_KEY);
      }
    } catch {
      // no-op
    }

    const savedConsent = localStorage.getItem(CONSENT_KEY);
    const savedPreferences = localStorage.getItem(PREFERENCES_KEY);

    if (savedConsent) {
      setConsentStatus(savedConsent as ConsentStatus);
      if (savedPreferences) {
        setPreferences(JSON.parse(savedPreferences));
      }
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  const persist = (
    status: ConsentStatus,
    prefs: CookiePreferences,
    action: 'accept_all' | 'reject_all' | 'customize',
    view: 'banner' | 'settings_modal' = 'banner'
  ) => {
    setPreferences(prefs);
    setConsentStatus(status);
    localStorage.setItem(CONSENT_KEY, status);
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
    localStorage.setItem(CONSENT_TS_KEY, String(Date.now()));
    updateGTMConsent(prefs, { action, status, view });
    setShowBanner(false);
  };

  const acceptAll = (view: 'banner' | 'settings_modal' = 'banner') => {
    persist('accepted', { essential: true, analytics: true, marketing: true }, 'accept_all', view);
  };

  const rejectAll = (view: 'banner' | 'settings_modal' = 'banner') => {
    persist('rejected', { essential: true, analytics: false, marketing: false }, 'reject_all', view);
  };

  const savePreferences = (prefs: CookiePreferences) => {
    persist('customized', { ...prefs, essential: true }, 'customize', 'settings_modal');
  };

  const resetConsent = () => {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(PREFERENCES_KEY);
    localStorage.removeItem(CONSENT_TS_KEY);
    setConsentStatus('pending');
    setPreferences({
      essential: true,
      analytics: false,
      marketing: false,
    });
    setShowBanner(true);
  };

  return {
    consentStatus,
    preferences,
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    setShowBanner,
  };
};
